import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { task, timeout } from 'ember-concurrency';
import { sortBy, prop } from 'ramda';
import { or } from 'ember-truth-helpers';
import getFeatureObjects from 'cpc-license-manager/constants/eflex-features';
import StandardModal from 'cpc-license-manager/components/standard-modal';
import FaBadgeIcon from 'cpc-license-manager/components/fa-badge-icon';
import BsForm from 'cpc-license-manager/components/bs-form';
import OptionsCallout from 'cpc-license-manager/components/options-callout';
class LicenseModal extends Component {
    @service
    store;
    @service
    organizationRepo;
    @tracked
    organizations = this.store.peekAll('organization');
    get level1Collapsed() {
        return this.level1Features.every((el)=>el.enabled);
    }
    get level2Collapsed() {
        return this.level2Features.every((el)=>el.enabled);
    }
    get level3Collapsed() {
        return this.level3Features.every((el)=>el.enabled);
    }
    get addonsCollapsed() {
        return this.addonFeatures.every((el)=>el.enabled);
    }
    get level1Features() {
        return this.featureOptions.filter((option)=>option.group === 'level-1');
    }
    get level2Features() {
        return this.featureOptions.filter((option)=>option.group === 'level-2');
    }
    get level3Features() {
        return this.featureOptions.filter((option)=>option.group === 'level-3');
    }
    get addonFeatures() {
        return this.featureOptions.filter((option)=>option.group === 'addon');
    }
    get featureOptions() {
        return sortBy(prop('label'), getFeatureObjects(this.args.license));
    }
    get saveDisabled() {
        return this.args.license?.validations.isInvalid || !this.args.license?.isDirty;
    }
    get confirmDisabled() {
        return this.args.isConfirming && isEmpty(this.args.changelog?.notes);
    }
    searchOrganizations = task(waitFor(async (term)=>{
        try {
            await timeout(300);
            return await this.organizationRepo.search.perform(term);
        } catch (e) {
            console.error(e);
        }
    }));
    @action
    async onConfirm() {
        if (this.args.isConfirming) {
            await this.args.confirmAction();
        } else {
            await this.args.saveAction();
        }
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    <StandardModal
      class="license-modal"
      @centered={{true}}
      @scrollable={{true}}
      @size="xl"
      @onCancel={{@cancelAction}}
      @onConfirm={{this.onConfirm}}
      ...attributes
      as |modal|
    >
      <modal.header>
        <h5 class="modal-title mb-0">
          {{#if @isEditing}}
            <FaBadgeIcon @mainIcon="key" @topIcon="pencil-alt" class="me-3" @badgeClass="bg-secondary" />
            Edit License -
            <span class="license-id">
              {{@license.licenseId}}
            </span>
          {{else}}
            <FaBadgeIcon @mainIcon="key" @topIcon="plus-square" class="me-3" @badgeClass="bg-success" />
            Add License
          {{/if}}
        </h5>
      </modal.header>
      <modal.body>
        {{#if @isConfirming}}
          <BsForm @model={{@changelog}} as |form|>
            <form.element
              @controlType="textarea"
              @label="Change Notes"
              @property="notes"
              class="license-change-notes"
            />
          </BsForm>
        {{else}}
          <BsForm @model={{@license}} as |form|>
            {{#if @showOrganization}}
              <form.element
                @controlType="power-select"
                @label="Organization"
                @property="organization"
                @options={{this.organizations}}
                class="license-organization"
                as |el|
              >
                <el.control
                  @searchField="name"
                  @searchEnabled={{true}}
                  @search={{this.searchOrganizations.perform}}
                  @renderInPlace={{true}}
                  placeholder="---"
                  as |org|
                >
                  {{org.name}}
                </el.control>
              </form.element>
            {{/if}}
            <div class="row form-row">
              <div class="col-md-6 col-sm-12">
                <form.element @controlType="text" @label="Label" @property="label" class="license-label" as |el|>
                  <el.control placeholder="License Label" />
                </form.element>
              </div>
              <div class="col-md-6 col-sm-12">
                <form.element
                  @controlType="text"
                  @label="Job Number"
                  @property="jobNumber"
                  class="license-job-number"
                  as |el|
                >
                  <el.control placeholder="Job Number" />
                </form.element>
              </div>
            </div>
            <div class="row form-row">
              <div class="col-md-6 col-sm-12">
                <form.element
                  @controlType="number"
                  @label="Station Limit"
                  @property="stationLimit"
                  class="license-station-limit"
                  as |el|
                >
                  <el.control placeholder="Station Limit" />
                </form.element>
              </div>
              <div class="col-md-6 col-sm-12">
                <form.element
                  @controlType="date-time-picker"
                  @label="Expiration Date"
                  @property="expirationDate"
                  class="license-expiration-date"
                  as |el|
                >
                  <el.control @dateFormat="Y-m-d" placeholder="Expiration Date" />
                </form.element>
                <form.element @controlType="checkbox" @label="Send Expiration Warnings" @property="sendWarnings" />
              </div>
            </div>
            <div class="row form-row">
              <div class="col">
                <form.element @controlType="textarea" @label="Notes" @property="notes" class="license-notes" />
              </div>
            </div>
            <OptionsCallout
              @calloutClass="callout-license-level-1"
              @title="Level 1 Features"
              @collapsed={{this.level1Collapsed}}
              @options={{this.level1Features}}
              @optionsGroup="level-1"
              @optionChanged={{@featureUpdateAction}}
              @checkAll={{@featureGroupUpdateAction}}
            />
            <OptionsCallout
              @calloutClass="callout-license-level-2"
              @title="Level 2 Features"
              @collapsed={{this.level2Collapsed}}
              @options={{this.level2Features}}
              @optionsGroup="level-2"
              @optionChanged={{@featureUpdateAction}}
              @checkAll={{@featureGroupUpdateAction}}
            />
            <OptionsCallout
              @calloutClass="callout-license-level-3"
              @title="Level 3 Features"
              @collapsed={{this.level3Collapsed}}
              @options={{this.level3Features}}
              @optionsGroup="level-3"
              @optionChanged={{@featureUpdateAction}}
              @checkAll={{@featureGroupUpdateAction}}
            />
            <OptionsCallout
              @calloutClass="callout-license-addon"
              @title="Addons"
              @collapsed={{this.addonsCollapsed}}
              @options={{this.addonFeatures}}
              @optionsGroup="addon"
              @optionChanged={{@featureUpdateAction}}
              @checkAll={{@featureGroupUpdateAction}}
            />
          </BsForm>
        {{/if}}
      </modal.body>
      <modal.footer as |footer|>
        <footer.close class="btn-sm" />
        <footer.confirm
          class="btn-sm btn-success"
          @disabled={{or this.confirmDisabled this.saveDisabled}}
        >
          {{#if @isConfirming}}
            Confirm
          {{else}}
            Save
          {{/if}}
        </footer.confirm>
      </modal.footer>
    </StandardModal>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default LicenseModal;
